import * as React from 'react'
import { CheckCircle2, XCircle, Info, X } from 'lucide-react'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ToastVariant = 'success' | 'error' | 'info'

interface Toast {
  id: string
  variant: ToastVariant
  message: string
}

// ---------------------------------------------------------------------------
// State management via module-level store + listeners (avoids context)
// ---------------------------------------------------------------------------

type Listener = () => void

let toasts: Toast[] = []
const listeners = new Set<Listener>()

function emitChange() {
  for (const listener of listeners) {
    listener()
  }
}

function addToast(variant: ToastVariant, message: string) {
  const id = crypto.randomUUID()
  toasts = [...toasts, { id, variant, message }]
  emitChange()

  // Auto-dismiss: 8 seconds for errors, 5 seconds for others
  const duration = variant === 'error' ? 8000 : 5000
  setTimeout(() => {
    dismissToast(id)
  }, duration)

  return id
}

function dismissToast(id: string) {
  toasts = toasts.filter((t) => t.id !== id)
  emitChange()
}

/**
 * Drops every queued toast at once.
 *
 * The store lives at module level so it survives unmounts — which is what makes
 * toasts work across navigation, and also what makes them leak between tests.
 * Tests call this in `afterEach` so one test's toast cannot be counted, or
 * silently auto-dismissed, inside another.
 */
function dismissAllToasts() {
  if (toasts.length === 0) return
  toasts = []
  emitChange()
}

function subscribe(listener: Listener) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): Toast[] {
  return toasts
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

function useToast() {
  const currentToasts = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const toast = React.useMemo(
    () => ({
      success: (message: string) => addToast('success', message),
      error: (message: string) => addToast('error', message),
      info: (message: string) => addToast('info', message),
      dismiss: dismissToast,
    }),
    []
  )

  return { toasts: currentToasts, toast }
}

// ---------------------------------------------------------------------------
// Visual components
// ---------------------------------------------------------------------------

const variantConfig: Record<
  ToastVariant,
  { icon: React.ElementType; className: string }
> = {
  success: {
    icon: CheckCircle2,
    className: 'border-success/30 bg-success/10 text-success',
  },
  error: {
    icon: XCircle,
    className: 'border-destructive/30 bg-destructive/10 text-destructive',
  },
  info: {
    icon: Info,
    className: 'border-primary/30 bg-primary/10 text-primary',
  },
}

interface ToastItemProps {
  toast: Toast
  onDismiss: (id: string) => void
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const config = variantConfig[toast.variant]
  const Icon = config.icon

  return (
    <div
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-lg border p-4 shadow-lg animate-fade-up',
        'bg-card',
        config.className
      )}
      role="status"
      aria-live="polite"
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      <p className="flex-1 text-sm font-medium text-foreground">
        {toast.message}
      </p>
      <button
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

function Toaster() {
  const { toasts: currentToasts, toast } = useToast()

  if (currentToasts.length === 0) return null

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
      aria-label="Notifications"
    >
      {currentToasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={toast.dismiss} />
      ))}
    </div>
  )
}

export { Toaster, useToast, dismissAllToasts }
export type { Toast, ToastVariant }
